import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { filter, firstValueFrom } from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { ImportazioniService } from 'src/app/services/importazioni/importazioni.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

type TipoElemento =
  | 'stakeholder'
  | 'impatto'
  | 'impegno'
  | 'azione'
  | 'kpi'
  | 'tabVsme'
  | 'certificazione'
  | 'UM';

export interface DialogImportazioneData {
  titoloImportazione: string;
  subTitoloImportazione: string;
  tipoElemento: TipoElemento;
  azienda: boolean;
  nomeImport: string;
}

@Component({
  selector: 'app-dialog-importazione-excel',
  templateUrl: './dialog-importazione-excel.component.html',
  styleUrls: ['./dialog-importazione-excel.component.scss'],
  standalone: false,
})
export class DialogImportazioneExcelComponent {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  public descrizioneErrore: string | undefined = undefined;
  public isIconaGuida: boolean = false;

  constructor(
    public dialog: MatDialog,
    private readonly utilityService: UtilityService,
    private readonly importazioniService: ImportazioniService,

    @Inject(MAT_DIALOG_DATA)
    public data: DialogImportazioneData,
  ) {}

  public fileSelezionato(file: File[]) {
    if (file[0].type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      this.isIconaGuida = false;
      this.spinnerOver.show();

      this.importazioniService
        .importFile(file[0], {}, this.data.azienda, this.data.nomeImport)
        .pipe(filter((res) => res?.type === 4))
        .subscribe({
          next: (result: any) => {
            console.warn(result);

            this.utilityService.openSnackBar('Salvataggio effettuato ', '', {
              duration: 2000,
              panelClass: ['success-snackbar'],
            });

            if (result.body) {
              let tableHTML =
                '<table style="border-collapse: collapse; padding: 10px;" border="1">';
              for (const riga in result.body) {
                if (result.body.hasOwnProperty(riga)) {
                  tableHTML += '<tr>';
                  if (riga === '-1') {
                    tableHTML += `<td colspan="2" style="color:var(--mat-color-accent); padding: 5px 10px 5px; text-align: center; ">${result.body[riga]}<br></td>`;
                  } else {
                    tableHTML += `<td style="color:var(--mat-color-accent); padding: 5px 10px 5px; font-size: 15px;" >Riga ${riga} : </td>`;
                    tableHTML += '<td>';
                    for (const msgErrore of result.body[riga]) {
                      tableHTML += `<span style="font-size: smaller; padding: 5px 10px 5px">- ${msgErrore}</span><br>`;
                    }
                    tableHTML += '</td>';
                  }
                  tableHTML += '</tr>';
                }
              }
              tableHTML += '</table>';

              this.dialog.closeAll();

              this.utilityService.openDialog({
                titolo: 'Esito Importazione',
                descrizione: tableHTML,
                bottoni: [{ nome_btn: 'Chiudi' }],
              });
            }
            this.spinnerOver.hide();
          },
          error: (err: any) => {
            console.error(err);
            this.utilityService.openDialog({
              titolo: 'Attenzione',
              descrizione: 'Errore nel salvataggio ',
              bottoni: [{ nome_btn: 'Chiudi' }],
            });
            this.spinnerOver.hide();
          },
        });
    } else {
      this.descrizioneErrore =
        " Attenzione! Il file d'importazione deve essere un file excel (.xlsx)";
      this.isIconaGuida = true;
    }
  }

  async scaricaTemplate() {
    // Percorso al tuo file Excel template
    const { base64Data, fileName } = await this.getTemplateFile();

    if (base64Data && fileName) {
      this.utilityService.downloadExcel(base64Data, fileName);
      this.utilityService.openSnackBar(`Esportazione completata`, '', {
        duration: 3000,
        panelClass: ['success-snackbar'],
      });
    }
  }

  /**
   * Recupera il file template Excel in base al tipo elemento.
   * @returns Oggetto contenente base64 del file e nome file.
   */
  async getTemplateFile(): Promise<{ base64Data: string; fileName: string }> {
    const mapTipoToWs: Record<TipoElemento, string> = {
      stakeholder: 'stakeholders',
      impatto: 'impatti',
      impegno: 'impegni',
      azione: 'azioni-e-buone-pratiche',
      kpi: 'kpi',
      certificazione: 'certificazioni',
      UM: 'unita-di-misura',
      tabVsme: 'tab-vsme',
    };

    const nomeWs = mapTipoToWs[this.data.tipoElemento] ?? '';

    if (!nomeWs) {
      this.utilityService.openSnackBar('Errore durante il download del template', '', {
        duration: 3000,
        panelClass: ['red-snackbar'],
      });
      return { base64Data: '', fileName: '' };
    }

    try {
      const esito = await firstValueFrom(this.importazioniService.getTemplateExcel(nomeWs));

      if (esito?.file) {
        return {
          base64Data: esito.file,
          fileName: esito.fileName,
        };
      }
    } catch (_) {
      // eventuale gestione errore silenziosa o logging
    }

    this.utilityService.openSnackBar('Errore durante il download del template', '', {
      duration: 3000,
      panelClass: ['red-snackbar'],
    });

    return { base64Data: '', fileName: '' };
  }
}
